// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

export interface Brokerage {
  id?: string; // UUID
  name: string;
  subdomain: string;
  /**
   * The actual Keycloak realm name this brokerage's advisor users live in.
   * Set by resource-server at brokerage-creation time. In some environments
   * this matches the subdomain ("gtpf"); in others it's "brokerage-{subdomain}"
   * — never assume one or the other when constructing realm URLs.
   */
  keycloakRealmId?: string;
  registrationNumber?: string;
  contactEmail?: string;
  contactPhone?: string;
  // Address fields (ISO 19160 compliant)
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  townCity?: string;
  stateProvinceCounty?: string;
  postalCode?: string;
  countryCode?: string;
  country?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  // Email configuration for sending emails on behalf of the brokerage
  emailAddress?: string;
  emailDisplayName?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUsername?: string;
  smtpPassword?: string;
  smtpUseTls?: boolean;
  smtpUseSsl?: boolean;
  // Branding (tier, logo, colors, JSONB config) is served from branding-service —
  // consume it via brandingApi (/api/branding/**), not the Brokerage shape.
  advisorInvitationTemplate?: string;
  clientInvitationTemplate?: string;
  advisorInvitationSubject?: string;
  clientInvitationSubject?: string;
  // CRM Integration
  crmSolutionName?: string; // CRM system used by the brokerage
  // SSO / login-method toggle. When false, advisors at this brokerage MUST
  // sign in via one of the configured SSO providers (see Integrations tab).
  // Default true. Toggled via PATCH /{id}/password-login.
  passwordLoginEnabled?: boolean;
}

export interface EmailTemplates {
  advisorInvitationTemplate?: string;
  clientInvitationTemplate?: string;
  advisorInvitationSubject?: string;
  clientInvitationSubject?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

const API_BASE = '/api/brokerages';

export const brokerageApi = {
  async getAll(params?: {
    page?: number;
    size?: number;
  }): Promise<PageResponse<Brokerage>> {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.size !== undefined) queryParams.append('size', String(params.size));

    const queryString = queryParams.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${API_BASE}${suffix}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch brokerages');
    }
    const data = await response.json();
    return data;
  },

  async getById(id: string): Promise<Brokerage> {
    const response = await fetch(`${API_BASE}/${id}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch brokerage');
    }
    return response.json();
  },

  async create(brokerage: Omit<Brokerage, 'id'>): Promise<Brokerage> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(brokerage)
    });
    if (!response.ok) {
      throw new Error('Failed to create brokerage');
    }
    return response.json();
  },

  async update(id: string, brokerage: Partial<Brokerage>): Promise<Brokerage> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(brokerage)
    });
    if (!response.ok) {
      throw new Error('Failed to update brokerage');
    }
    return response.json();
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete brokerage');
    }
  },

  // NOTE: updateLogo / updateBranding were removed. Use brandingApi.updateLogos,
  // brandingApi.updateBranding, brandingApi.updateTier — they target branding-service.

  async updateEmailTemplates(id: string, templates: EmailTemplates): Promise<Brokerage> {
    const response = await fetch(`${API_BASE}/${id}/email-templates`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(templates)
    });
    if (!response.ok) {
      throw new Error('Failed to update email templates');
    }
    return response.json();
  },

  async getDefaultBranding(): Promise<Record<string, unknown>> {
    const response = await fetch(`${API_BASE}/default-branding`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch default branding');
    }
    return response.json();
  },

  async getDefaultEmailTemplates(): Promise<EmailTemplates> {
    const response = await fetch(`${API_BASE}/default-email-templates`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch default email templates');
    }
    return response.json();
  },

  /**
   * Toggle whether advisors at this brokerage can sign in with email +
   * password. Backed by PATCH /api/brokerages/{id}/password-login.
   *
   * The server refuses to turn this off when no SSO Identity Provider is
   * enabled — turning it off without an alternative sign-in path would lock
   * advisors out. That case surfaces as a 400 with a descriptive message;
   * the UI should validate before calling.
   */
  async setPasswordLoginEnabled(id: string, enabled: boolean): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}/password-login`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ enabled })
    });
    if (!response.ok) {
      // Try to surface the backend message (typically: "Cannot disable
      // password login when no SSO Identity Provider is enabled.")
      let detail = '';
      try {
        const body = await response.json();
        detail = body?.message ?? body?.detail ?? '';
      } catch {
        // ignore — fall back to a generic message
      }
      throw new Error(detail || `Failed to update password-login (HTTP ${response.status})`);
    }
  }
};
